import { useRef, useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import Logo from "../../static/assets/logo_rvg.png";
import { useTranslation } from "react-i18next";
import LoginFooter from "./LoginFooter.js";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { MsalProvider } from "@azure/msal-react";
import MicrosoftLoginButton from "./MicrosoftLoginButton.js";
import { getMsalInstance } from "./msalConfig.js";
import {
  consumeMicrosoftSsoRedirectError,
  getSsoBackendErrorMessage,
  resolveAuthErrorMessage,
} from "./microsoftSsoRedirect.js";

const Login = ({ completeLogin, API }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const continueButtonRef = useRef(null);
  const [ssoButtonWidth, setSsoButtonWidth] = useState(0);
  const [msalInstance, setMsalInstance] = useState(null);

  useEffect(() => {
    const redirectError = consumeMicrosoftSsoRedirectError();
    if (redirectError !== null) {
      if (redirectError === "account_locked") {
        setErrorMessage(t("login.accountLocked"));
      } else {
        setErrorMessage(
          redirectError || t("Microsoft sign-in failed. Please try again."),
        );
      }
    }
  }, [t]);

  useEffect(() => {
    if (continueButtonRef.current) {
      setSsoButtonWidth(continueButtonRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (continueButtonRef.current) {
        setSsoButtonWidth(continueButtonRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const GOOGLE_SSO_CLIENT_ID = process.env.REACT_APP_GOOGLE_SSO_CLIENT_ID;
  const MICROSOFT_SSO_CLIENT_ID = process.env.REACT_APP_MICROSOFT_SSO_CLIENT_ID;

  const finishLogin = useCallback(
    async (userData) => {
      await completeLogin(userData);
      setErrorMessage("");
      history.push("/");
    },
    [completeLogin, history],
  );

  const handleSsoBackendLogin = useCallback(
    async (endpoint, token) => {
      const responseBackend = await axios.post(
        `${API}${endpoint}`,
        { body: token },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      await finishLogin(responseBackend.data);
    },
    [API, finishLogin],
  );

  useEffect(() => {
    if (!MICROSOFT_SSO_CLIENT_ID) {
      return undefined;
    }

    let isMounted = true;

    getMsalInstance()
      .then((instance) => {
        if (isMounted && instance) {
          setMsalInstance(instance);
        }
      })
      .catch((error) => {
        console.error(
          "Microsoft sign-in initialization failed:",
          error?.errorCode || error?.message || "unknown",
        );
      });

    return () => {
      isMounted = false;
    };
  }, [MICROSOFT_SSO_CLIENT_ID]);

  const handleGoogleLoginSuccess = async (response) => {
    try {
      await handleSsoBackendLogin("auth/google/callback", response.credential);
    } catch (error) {
      setErrorMessage(
        resolveAuthErrorMessage(error, t) ||
          getSsoBackendErrorMessage(error) ||
          t("Google sign-in failed. Please try again."),
      );
      console.error("Google sign-in failed:", error?.message || "unknown");
    }
  };

  const handleGoogleLoginError = () => {
    setErrorMessage(t("Google sign-in failed. Please try again."));
    console.error("Google sign-in failed");
  };

  const handleMicrosoftLoginError = (error) => {
    if (error?.errorCode === "user_cancelled") {
      return;
    }

    setErrorMessage(t("Microsoft sign-in failed. Please try again."));

    console.error(
      "Microsoft sign-in failed:",
      error?.errorCode || error?.message || "unknown",
    );
  };

  useEffect(() => {
    const themeColor = document.querySelector('meta[name="theme-color"]');
    themeColor.setAttribute("content", "#f5f5f5");
    return () => {
      themeColor.setAttribute("content", "#00A7A6");
    };
  }, []);

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      const response = await axios.post(`${API}user/login`, {
        email: email,
        password: password,
      });

      await finishLogin(response.data);
    } catch (error) {
      setErrorMessage(
        resolveAuthErrorMessage(error, t) ||
          error.response?.data?.message ||
          t("login.invalidCredentials"),
      );
    }
  };

  return (
    <div className="login-page">
      <div className="header">
        <img src={Logo} alt="Logo Akanthas" className="logo" />
      </div>
      <div className="login-container">
        <div className="login-box">
          <h1 className="sign-in-message">{t("Jump in!")}</h1>{" "}
          <p className="tagline">
            {t(
              "Together, we're not just managing waste, we're changing the world.",
            )}
          </p>
          <p className="tagline"></p>
          <form onSubmit={handleSubmit} className="login-form">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(event) => {
                const value = event.target.value;

                const cleanedValue = value.replace(/\s/g, "");
                setEmail(cleanedValue);
                setErrorMessage("");
              }}
              className={errorMessage === "invalid_email" ? "error" : ""}
              required
            />
            {errorMessage === "invalid_email" && (
              <p className="error-message">{t("Invalid email address")}</p>
            )}
            <div className="password-field">
              <input
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setErrorMessage("");
                }}
                className="password-input"
                required
              />

              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              >
                <FontAwesomeIcon
                  icon={isPasswordVisible ? faEye : faEyeSlash}
                />
              </button>

              {errorMessage === "invalid_password" && (
                <p className="error-message">{t("Invalid password")}</p>
              )}
            </div>
            {errorMessage &&
              errorMessage !== "invalid_email" &&
              errorMessage !== "invalid_password" && (
                <p className="error-message global-error">{errorMessage}</p>
              )}
            <button
              onClick={() => history.push("/forgotpassword")}
              type="button"
              className="back-link"
            >
              {t("Forgot password?")}
            </button>
            <button
              ref={continueButtonRef}
              type="submit"
              className="login-button"
            >
              {t("Continue")}
            </button>

            <div className="divider">
              <span>{t("OR")}</span>
            </div>
            <div className="sso-buttons">
              <GoogleOAuthProvider
                clientId={GOOGLE_SSO_CLIENT_ID}
                style={{ width: "100%" }}
              >
                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={handleGoogleLoginError}
                  width={ssoButtonWidth}
                />
              </GoogleOAuthProvider>
              {msalInstance && MICROSOFT_SSO_CLIENT_ID && (
                <MsalProvider instance={msalInstance}>
                  <MicrosoftLoginButton
                    onError={handleMicrosoftLoginError}
                    onAttempt={() => setErrorMessage("")}
                    width={ssoButtonWidth}
                  />
                </MsalProvider>
              )}
            </div>
          </form>
        </div>
      </div>

      <LoginFooter />
    </div>
  );
};

export default Login;
